import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { ListItemText, Box, Grid } from '@material-ui/core';

import { AnimatedCounter, BasicThreeDotsMenu, StyledChip } from 'components/shared';
import { ApplicationState } from 'store';
import { images } from 'assets';
import styles from '../../styles';
// @ts-ignore
import './print-styles.css';
import financeStore from 'store/financeStore';
import { ChartBarCheckbox, Charts } from './components/Charts';
import DateFilters from '../DateFIlters';
import FinanceTotalAmount from '../TotalAmount';
import { theme } from 'config/theme';
import dayjs from 'dayjs';
import KPICard from '../../shared/kpiCard';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { FinanceSalesTable } from './components/financeSalesTable';
import { helperFunctions } from 'helpers';
import { OrderServices } from 'constants/enums';
import { printElement } from '../../shared/printHelper';

export interface DateHistoryData {
    name: string;
    startDate: Date;
    endDate: Date;
    isCustom?: boolean;
}

interface FinanceProps {
    restaurantId?: string;
    isOld?: boolean;
}

const Finances = ({ restaurantId = '', isOld = false }: FinanceProps) => {
    const classes = styles();
    const dispatch = useDispatch();
    const sales = useSelector((state: ApplicationState) => state.finance.financeSales);
    const netPaidSales =
        sales.totalAmount - sales.refundsAmount - sales.paidStorno?.totalAmount || 0;
    const currency = useSelector(
        (state: ApplicationState) =>
            state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const [dateFilter, setDateFilter] = useState(0);
    const [chartView, setChartView] = useState(false);
    const [selectedDatesHistory, setSelectedDatesHistory] = useState<DateHistoryData[]>([]);
    const [hoveredSwitchIcon, setHoveredSwitchIcon] = useState(0);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const orderChartDataList = (sales.orders || []).map((order) => ({
        name: order.service === OrderServices.TakeOut ? OrderServices.DineIn : order.service,
        date: order.createDate,
        amount: order.totalAmount,
    }));
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getFinance(1);
    }, []);

    function getFinance(
        dayAgo: number = 0,
        dateHistoryList?: DateHistoryData[],
        dateFrom?: Date,
        dateTo?: Date
    ) {
        if (!!!dateFrom && !!!dateTo) {
            dateFrom = new Date();
            dateTo = new Date();
            dateFrom.setDate(dateFrom.getDate() - dayAgo);
        }

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);

        dispatch(
            financeStore.actionCreators.getFinance(
                formatDateForServer(dateFrom!),
                formatDateForServer(dateTo!),
                restaurantId
            )
        );
    }

    // Format dates to maintain local time and avoid timezone conversion
    function formatDateForServer(date: Date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

        // Format as ISO string but with local time (no timezone conversion)
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    function exportFinance() {
        var from = formatDateForServer(dateFrom ?? dayjs().subtract(1, 'day').toDate());
        var to = formatDateForServer(dateTo ?? dayjs().toDate());

        dispatch(financeStore.actionCreators.getFinanceExcel(from, to, restaurantId));
    }

    function getIconSource(i: number) {
        if (hoveredSwitchIcon > 0) {
            if (i === 0 && hoveredSwitchIcon === 1) {
                return images.icons.listSelected;
            }
            if (i === 1 && hoveredSwitchIcon === 2) {
                return images.icons.graphSelected;
            }
        }

        switch (chartView) {
            case true:
                switch (i) {
                    case 0:
                        return images.icons.list;
                    case 1:
                        return images.icons.graphSelected;
                }
                break;
            default: {
                switch (i) {
                    case 0:
                        return images.icons.listSelected;
                    case 1:
                        return images.icons.graph;
                }
            }
        }
    }

    function handleDateRangeChange(from: Date | null, to: Date | null): void {
        setDateFrom(from);
        setDateTo(to);
    }

    function getTopServiceName(): React.ReactNode {
        if (sales.topServiceName === null) {
            return i18n.t('common.notAvailableShort');
        }

        return i18n.t(`form.${sales.topServiceName}`);
    }

    function getTopServiceSubText(): string {
        if (sales.topServiceName === null) {
            return i18n.t('common.notAvailableShort');
        }

        return i18n.t('finances.percentOfRevenue', { percent: sales.topServicePercent });
    }

    function getCheckboxList(): ChartBarCheckbox[] {
        return [
            {
                id: OrderServices.HomeDelivery,
                name: i18n.t('form.homeDelivery'),
                label: (
                    <Box display="flex" alignItems="center">
                        <img src={images.icons.chartHomeDelivery} alt="chart-icon" />
                        <span className={classes.chartBottomIconText}>
                            {i18n.t('form.homeDelivery')}
                        </span>
                    </Box>
                ),
            },
            {
                id: OrderServices.TakeAway,
                name: i18n.t('form.takeAway'),
                label: (
                    <Box display="flex" alignItems="center">
                        <img src={images.icons.takeAway} alt="chart-icon" />
                        <span className={classes.chartBottomIconText}>
                            {i18n.t('form.takeAway')}
                        </span>
                    </Box>
                ),
            },
            {
                id: OrderServices.DineIn,
                name: i18n.t('form.dineIn'),
                label: (
                    <Box display="flex" alignItems="center">
                        <img src={images.icons.chartDineIn} alt="chart-icon" />
                        <span className={classes.chartBottomIconText}>{i18n.t('form.dineIn')}</span>
                    </Box>
                ),
            },
        ];
    }

    function handlePrint(): void {
        printElement(containerRef);
    }

    return (
        <div
            ref={containerRef}
            className={classes.pBottom5}
            style={{ height: '100%', overflow: 'auto' }}
        >
            {!isOld && (
                <div className={classes.topContainer} style={{ justifyContent: 'space-between' }}>
                    <StyledChip
                        icon={<InfoOutlinedIcon />}
                        label={i18n.t('finances.filteredByOrderCreationDate')}
                        size="small"
                        labelFontSize={12}
                        labelColor={theme.palette.primary.dark}
                        variant="outlined"
                        color="primary"
                    />
                    <FinanceTotalAmount
                        style={{ position: 'static' }}
                        currency={currency}
                        title={i18n.t('finances.netPaidSales')}
                        amount={
                            <AnimatedCounter
                                value={netPaidSales}
                                formatFn={(val) => helperFunctions.formatNumber(val)}
                            />
                        }
                        breakdownItems={[
                            {
                                label: i18n.t('finances.grossPaidSales'),
                                amount: helperFunctions.formatNumber(sales.totalAmount),
                            },
                            {
                                label: i18n.t('finances.refunds'),
                                amount: `- ${helperFunctions.formatNumber(sales.refundsAmount)}`,
                                amountColor: 'negative',
                            },
                            {
                                label: i18n.t('finances.paidStorno'),
                                amount: `- ${helperFunctions.formatNumber(
                                    sales.paidStorno?.totalAmount || 0
                                )}`,
                                amountColor: 'negative',
                            },
                            {
                                label: i18n.t('finances.netPaidSales'),
                                amount: helperFunctions.formatNumber(netPaidSales),
                                amountColor: 'main',
                            },
                        ]}
                    />
                </div>
            )}

            <div className={classes.kpiContainer}>
                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.totalOrders')}
                                value={<AnimatedCounter value={sales.totalOrders} />}
                                sub={i18n.t('finances.inPeriod')}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.itemsSold')}
                                value={<AnimatedCounter value={sales.itemsSold} />}
                                sub={i18n.t('finances.paidItems')}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.avgOrderValue')}
                                value={
                                    <AnimatedCounter
                                        value={sales.avgOrderAmount}
                                        formatFn={(value) => `${currency} ${value.toFixed(2)}`}
                                    />
                                }
                                sub={i18n.t('finances.perOrder')}
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.paidStorno')}
                                value={<AnimatedCounter value={sales.paidStorno?.count || 0} />}
                                sub={
                                    ((sales.paidStorno?.count || 0) === 1
                                        ? i18n.t('common.item')
                                        : i18n.t('common.items')) +
                                    ` : ${currency} ${sales.paidStorno?.totalAmount || 0}`
                                }
                            />
                        </Grid>
                        <Grid item xs xl={2}>
                            <KPICard
                                label={i18n.t('finances.topService')}
                                value={getTopServiceName()}
                                sub={getTopServiceSubText()}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <div className={classes.dateContainer}>
                <div className={classes.flexRow}>
                    <DateFilters
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getFinance}
                        selectedDatesHistory={selectedDatesHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        isChartsShowing={chartView}
                        onDateRangeChange={handleDateRangeChange}
                    />
                    <span style={{ marginLeft: 20 }}></span>
                    <div className={classes.switchGraphContainer}>
                        <img
                            onClick={() => setChartView(false)}
                            onMouseOver={() => setHoveredSwitchIcon(1)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(0)}
                            alt="switch-gaph"
                        />
                        <img
                            onClick={() => {
                                setChartView(true);
                            }}
                            onMouseOver={() => setHoveredSwitchIcon(2)}
                            onMouseLeave={() => setHoveredSwitchIcon(0)}
                            className={classes.switchBasic}
                            src={getIconSource(1)}
                            alt="switch-gaph"
                        />
                    </div>
                    <BasicThreeDotsMenu
                        id="hide-in-print"
                        items={[
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={handlePrint}
                            >
                                <img
                                    src={images.icons.print}
                                    alt="print-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.print')} />
                            </div>,
                            <div
                                id="hide-in-print"
                                className={classes.threeDotsMenuItemContainer}
                                onClick={exportFinance}
                            >
                                <img
                                    src={images.icons.fileDownload}
                                    alt="file-icon"
                                    className={classes.threeDotsItemIcon}
                                />
                                <ListItemText primary={i18n.t('common.excelExport')} />
                            </div>,
                        ]}
                    />
                </div>
            </div>

            {chartView && (
                <Charts
                    selectedDatesHistory={selectedDatesHistory}
                    dateFilter={dateFilter}
                    onDateClick={(selectedDates, newDateFilter, dateHistoryList) => {
                        const { startDate, endDate } = selectedDates;
                        setSelectedDatesHistory(dateHistoryList);
                        return dispatch(
                            financeStore.actionCreators.getFinance(startDate, endDate, restaurantId)
                        );
                    }}
                    currency={currency}
                    dataList={orderChartDataList}
                    checkboxList={getCheckboxList()}
                />
            )}
            {!chartView && <FinanceSalesTable sales={sales} currency={currency} />}
        </div>
    );
};

export default Finances;
